import React from "react"

import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import { Tooltip } from "@coval/ui"
import { Clock, X as XIcon } from "lucide-react"

import { MetricErrorStatus } from "@/types/api/MetricOutput"
import type { TargetCondition } from "@/types/metrics/contracts"
import { formatMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

import {
  getMetricColor,
  yesNoBadgeColorClasses,
  yesNoBadgeEmptyClasses,
  yesNoBadgeShapeClasses,
  yesNoBadgeWarningClasses,
} from "../../../metric-display"

interface ReviewBadgeProps {
  value: string | null
  targetCondition?: TargetCondition
  commentOnly?: boolean
  emptyLabel?: string
  errorStatus?: string
  errorMessage?: string
}

const QUEUED_RUN_STATUS = "QUEUED" as const

export const ReviewBadge: React.FC<ReviewBadgeProps> = ({
  value,
  targetCondition,
  commentOnly,
  emptyLabel,
  errorStatus,
  errorMessage,
}) => {
  if (errorStatus === MetricErrorStatus.FAILED || errorStatus === "FAILED") {
    return (
      <Tooltip content={errorMessage || "Metric encountered an error"}>
        <div className="text-red-500 flex items-center justify-center">
          <XIcon className="h-5 w-5" />
        </div>
      </Tooltip>
    )
  }

  if (errorStatus === MetricErrorStatus.SKIPPED || errorStatus === "SKIPPED") {
    return (
      <Tooltip content="Metric was skipped">
        <div className="text-gray-500 flex items-center justify-center">—</div>
      </Tooltip>
    )
  }

  if (errorStatus === "CANCELLED" || errorStatus === "RUN_CANCELLED") {
    return (
      <Tooltip content="Run was canceled">
        <div className="text-blue-500 flex items-center justify-center">—</div>
      </Tooltip>
    )
  }

  if (
    errorStatus === MetricErrorStatus.IN_PROGRESS ||
    errorStatus === "IN_PROGRESS"
  ) {
    return (
      <Tooltip content="Metric is still running">
        <div className="flex items-center justify-center">
          <svg
            className="h-5 w-5 text-gray-500 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </div>
      </Tooltip>
    )
  }

  if (
    errorStatus === MetricErrorStatus.IN_QUEUE ||
    errorStatus === QUEUED_RUN_STATUS ||
    errorStatus === "IN_QUEUE"
  ) {
    return (
      <Tooltip content="Metric is queued for processing">
        <div className="flex items-center justify-center">
          <Clock className="h-5 w-5 text-gray-500" />
        </div>
      </Tooltip>
    )
  }

  if (!value) {
    if (emptyLabel) {
      return <span className="text-sm text-coval-text">{emptyLabel}</span>
    }
    if (commentOnly) {
      return <span className="text-sm text-foreground">No annotation</span>
    }
    return (
      <span className={`${yesNoBadgeShapeClasses} ${yesNoBadgeEmptyClasses}`}>
        N/A
      </span>
    )
  }

  const normalizedValue = value.toUpperCase()
  const isUnknown =
    normalizedValue === ANNOTATION_VALUE.UNKNOWN || normalizedValue === "N/A"
  const isError = value === "Error"

  if (isError) {
    return (
      <Tooltip content={errorMessage || "Metric encountered an error"}>
        <span
          className={`${yesNoBadgeShapeClasses} ${yesNoBadgeWarningClasses}`}
        >
          Err
        </span>
      </Tooltip>
    )
  }

  if (isUnknown) {
    return (
      <span
        className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses.default}`}
      >
        N/A
      </span>
    )
  }

  if (
    normalizedValue === ANNOTATION_VALUE.YES ||
    normalizedValue === ANNOTATION_VALUE.NO
  ) {
    const color = getMetricColor(normalizedValue, targetCondition)
    const displayText = normalizedValue === ANNOTATION_VALUE.YES ? "Yes" : "No"
    return (
      <span
        className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses[color]}`}
      >
        {displayText}
      </span>
    )
  }

  if (normalizedValue === "PASS" || normalizedValue === "FAIL") {
    const color = normalizedValue === "PASS" ? "success" : "danger"
    return (
      <span
        className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses[color]}`}
      >
        {normalizedValue === "PASS" ? "Pass" : "Fail"}
      </span>
    )
  }

  return (
    <span
      className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses.default}`}
    >
      {formatMetricDisplayName(value)}
    </span>
  )
}
